import * as React from 'react';
import {
  Alert,
  BottomSheet,
  BottomSheetContent,
  Button,
  CommentComposer,
  CommentList,
  ConfirmDialog,
  PhotoPager,
  Stack,
  type CommentItem,
  type PhotoListItem,
} from '@relay-towing/ui';
import { MessageCircle, Trash2 } from 'lucide-react';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import {
  useAddVehicleMediaComment,
  useDeleteVehicleMediaComment,
  useGetVehicleMediaComments,
} from '../../hooks/useVehicleMediaComments';

interface MediaComment {
  id: string;
  userId: string;
  text: string;
  user?: { name?: string };
  _optimistic?: boolean;
}

/**
 * One photo's comments, and adding and deleting them. The server lets a
 * member add a comment where they may change the vehicle, which is
 * `editable`, and delete only their own; the controls show to match.
 */
function usePhotoComments(vehicleId: string, mediaUrl: string | undefined) {
  const { data: profile } = useDriverProfile();
  const { data } = useGetVehicleMediaComments(vehicleId, mediaUrl);
  const add = useAddVehicleMediaComment();
  const remove = useDeleteVehicleMediaComment();

  const comments: CommentItem[] = (
    (data?.comments ?? []) as MediaComment[]
  ).map((comment) => ({
    id: comment.id,
    author: comment.user?.name ?? 'Someone',
    text: comment.text,
    pending: comment._optimistic,
    deletable: !!profile?.userId && comment.userId === profile.userId,
  }));

  return {
    comments,
    sending: add.isPending,
    deletingId: remove.isPending ? (remove.variables ?? null) : null,
    send: (text: string) =>
      mediaUrl
        ? add.mutateAsync({
            vehicleId,
            mediaUrl,
            text,
            userName: profile?.name,
          })
        : Promise.resolve(false as const),
    delete: (id: string) => remove.mutate(id),
  };
}

/**
 * A photo on its own, as the Figma's Isolated, Comment frames draw it: the
 * picture, swiped sideways to the others, its comments under it, and Delete
 * and Comment along the foot. Comment turns the foot into the comment box.
 * Open while `index` is set.
 */
export function PhotoSheet({
  vehicleId,
  photos,
  index,
  onIndexChange,
  onClose,
  editable,
  onDelete,
  deleting,
}: {
  vehicleId: string;
  photos: PhotoListItem[];
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** May change the vehicle: comment on and delete its photos. */
  editable: boolean;
  /** Deletes the photo at this URL; the sheet has already asked. */
  onDelete: (src: string) => Promise<unknown>;
  deleting: boolean;
}) {
  // A deleted photo leaves the list, so the index may now be one past it.
  const shown = index === null ? null : Math.min(index, photos.length - 1);
  const photo = shown === null || shown < 0 ? undefined : photos[shown];
  const ready = !!photo?.src && !photo.status;
  const comments = usePhotoComments(
    vehicleId,
    ready ? (photo?.src ?? undefined) : undefined
  );
  const [writing, setWriting] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setWriting(false);
    setFailed(false);
  }, [shown]);

  const remove = async () => {
    if (!photo?.src) return;
    try {
      await onDelete(photo.src);
      setConfirming(false);
      if (photos.length <= 1) onClose();
    } catch {
      setConfirming(false);
      setFailed(true);
    }
  };

  return (
    <BottomSheet
      open={photo !== undefined}
      onOpenChange={(open) => !open && onClose()}
    >
      <BottomSheetContent
        title={`Image ${(shown ?? 0) + 1}`}
        size="full"
        hideCloseButton
        footer={
          !editable || !ready ? undefined : writing ? (
            <CommentComposer
              autoFocus
              sending={comments.sending}
              onSubmit={async (text) => {
                await comments.send(text);
                setWriting(false);
              }}
            />
          ) : (
            <>
              <Button
                variant="destructive-secondary"
                size="xl"
                pill
                leadingIcon={<Trash2 />}
                onClick={() => setConfirming(true)}
              >
                Delete
              </Button>
              <Button
                variant="secondary"
                size="xl"
                pill
                leadingIcon={<MessageCircle />}
                onClick={() => setWriting(true)}
              >
                Comment
              </Button>
            </>
          )
        }
      >
        <Stack gap={4}>
          <PhotoPager
            photos={photos}
            index={shown ?? 0}
            onIndexChange={onIndexChange}
          />
          {failed ? (
            <Alert tone="warning">The photo was not deleted. Try again.</Alert>
          ) : null}
          <CommentList
            heading="Comments"
            comments={comments.comments}
            onDelete={comments.delete}
            deletingId={comments.deletingId}
          />
        </Stack>
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Delete this photo?"
          description="It goes from the call for everyone, with its comments. This cannot be undone."
          confirmLabel="Delete photo"
          destructive
          loading={deleting}
          onConfirm={() => void remove()}
        />
      </BottomSheetContent>
    </BottomSheet>
  );
}

/**
 * A photo's comments over the Photos list, as the Figma's Quick Comment
 * frames draw them: the comments, and the box at the foot to add one, so a
 * driver can say something about a photo without opening it.
 */
export function QuickCommentSheet({
  vehicleId,
  src,
  onClose,
  editable,
}: {
  vehicleId: string;
  /** The photo being commented on; open while set. */
  src: string | null;
  onClose: () => void;
  editable: boolean;
}) {
  const comments = usePhotoComments(vehicleId, src ?? undefined);
  return (
    <BottomSheet
      open={src !== null}
      onOpenChange={(open) => !open && onClose()}
    >
      <BottomSheetContent
        title="Comments"
        footer={
          editable ? (
            <CommentComposer
              sending={comments.sending}
              onSubmit={comments.send}
            />
          ) : undefined
        }
      >
        <CommentList
          comments={comments.comments}
          onDelete={comments.delete}
          deletingId={comments.deletingId}
        />
      </BottomSheetContent>
    </BottomSheet>
  );
}
